import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = (process.env.REACT_APP_API_BASE || '/api').replace(/["']/g, '').replace(/\/+$/, '');

export default function ManageTags() {
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  
  // Form State
  const [formData, setFormData] = useState({
    subject: 'math',
    tag_type: 'question',
    code: '',
    english_meaning: '',
    chinese_meaning: ''
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { fetchTags(); }, []);

  const fetchTags = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/tag-definitions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Sort by subject then code
        data.sort((a, b) => a.subject.localeCompare(b.subject) || a.code.localeCompare(b.code));
        setTags(data);
      }
    } catch (err) {
      setError('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    const url = editingId 
      ? `${API_BASE}/admin/tag-definitions/${editingId}` 
      : `${API_BASE}/admin/tag-definitions`;
    
    const method = editingId ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify(formData)
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.error || 'Failed to save tag');
      }

      setSuccessMsg(editingId ? 'Tag updated successfully!' : 'Tag added successfully!');
      resetForm();
      fetchTags(); // Refresh list
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (tag) => {
    setFormData({
      subject: tag.subject,
      tag_type: tag.tag_type,
      code: tag.code,
      english_meaning: tag.english_meaning || '',
      chinese_meaning: tag.chinese_meaning || ''
    });
    setEditingId(tag.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setFormData({ subject: 'math', tag_type: 'question', code: '', english_meaning: '', chinese_meaning: '' });
    setEditingId(null);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 1200, margin: '40px auto', padding: 24, background: '#f8f9fa', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>🏷️ Manage Tag Definitions</h2>
        <button onClick={() => navigate(-1)} style={{ padding: '8px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>← Back</button>
      </div>

      {/* ✅ Form Section */}
      <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
        <h3 style={{ marginTop: 0, marginBottom: 15 }}>{editingId ? 'Edit Tag' : 'Add New Tag'}</h3>
        
        {error && <div style={{ padding: 10, background: '#f8d7da', color: '#721c24', borderRadius: 4, marginBottom: 15 }}>{error}</div>}
        {successMsg && <div style={{ padding: 10, background: '#d4edda', color: '#155724', borderRadius: 4, marginBottom: 15 }}>{successMsg}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 15, marginBottom: 15 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>Subject</label>
              <select name="subject" value={formData.subject} onChange={handleInputChange} style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ced4da' }}>
                <option value="math">Math</option>
                <option value="english">English</option>
                <option value="science">Science</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>Tag Type</label>
              <select name="tag_type" value={formData.tag_type} onChange={handleInputChange} style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ced4da' }}>
                <option value="question">Question (Skill)</option>
                <option value="error">Error (Misconception)</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>Code (e.g., M-S-C1)</label>
              <input 
                name="code" 
                value={formData.code} 
                onChange={handleInputChange} 
                required 
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ced4da' }} 
                placeholder="Unique Code"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15, marginBottom: 15 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>English Meaning</label>
              <input 
                name="english_meaning" 
                value={formData.english_meaning} 
                onChange={handleInputChange} 
                required 
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ced4da' }} 
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>Chinese Meaning</label>
              <input 
                name="chinese_meaning" 
                value={formData.chinese_meaning} 
                onChange={handleInputChange} 
                required 
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ced4da' }} 
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" style={{ padding: '10px 20px', background: '#007BFF', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
              {editingId ? 'Update Tag' : 'Add Tag'}
            </button>
            {editingId && (
              <button type="button" onClick={resetForm} style={{ padding: '10px 20px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>

      {/* ✅ List Section */}
      <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
        <h3 style={{ marginTop: 0, marginBottom: 15 }}>Existing Tags ({tags.length})</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: 10, textAlign: 'left' }}>Code</th>
                <th style={{ padding: 10, textAlign: 'left' }}>Subject</th>
                <th style={{ padding: 10, textAlign: 'left' }}>Type</th>
                <th style={{ padding: 10, textAlign: 'left' }}>English Meaning</th>
                <th style={{ padding: 10, textAlign: 'left' }}>Chinese Meaning</th>
                <th style={{ padding: 10, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tags.map(tag => (
                <tr key={tag.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 10, fontFamily: 'monospace', fontWeight: 600 }}>{tag.code}</td>
                  <td style={{ padding: 10, textTransform: 'capitalize' }}>{tag.subject}</td>
                  <td style={{ padding: 10, textTransform: 'capitalize' }}>{tag.tag_type}</td>
                  <td style={{ padding: 10 }}>{tag.english_meaning}</td>
                  <td style={{ padding: 10 }}>{tag.chinese_meaning}</td>
                  <td style={{ padding: 10, textAlign: 'center' }}>
                    <button 
                      onClick={() => handleEdit(tag)} 
                      style={{ padding: '4px 10px', background: '#ffc107', color: '#000', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}